import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { API, axios } from '../../utils/api';
import Icona from '../cliente/Icone';
import { errore, quandoBreve } from './comuni';

// Impostazioni → Aste (backend/aste_apprendimento.py): quello che il sistema ha imparato dalle aste che lo staff
// segna con «È un'asta» nei nuovi annunci, nelle case in archivio e nella scheda della casa.
// - le agenzie contate: alla seconda asta l'agenzia passa ad «aste» ed esce da tutte le proposte;
// - le parole da aggiungere: quelle frequenti negli annunci segnati e rare in quelli normali. Un clic le accetta e
//   da lì in poi valgono come le parole forti del filtro (backend/aste.py); «No, grazie» non le ripropone più.

const Aste = () => {
  const [dati, setDati] = useState(null);
  const [attesa, setAttesa] = useState(null);
  const carica = useCallback(() => axios.get(`${API}/admin/aste`).then((r) => setDati(r.data)).catch(() => setDati(false)), []);
  useEffect(() => { carica(); }, [carica]);

  const parola = async (testo, azione) => {
    setAttesa(testo);
    try {
      const r = await axios.post(`${API}/admin/aste/parole`, { parola: testo, azione });
      setDati(r.data);
      const tolte = r.data?.tolti?.proposte || 0;
      toast.success(azione === 'accetta'
        ? `«${testo}» vale come le altre parole delle aste${tolte ? ` · ${tolte === 1 ? '1 annuncio tolto' : `${tolte} annunci tolti`} dalle proposte` : ''}`
        : `«${testo}» non verrà più proposta`);
    } catch (e) { toast.error(errore(e, 'Non riuscito')); } finally { setAttesa(null); }
  };

  const ripassa = async () => {
    setAttesa('ripassa');
    try {
      const r = await axios.post(`${API}/admin/aste/ripassa`);
      setDati(r.data);
      const t = r.data?.tolti || {};
      toast.success(t.proposte || t.immobili ? `Tolti ${t.proposte} annunci e ${t.immobili} case dalle proposte` : 'Niente da togliere: tutto a posto');
    } catch (e) { toast.error(errore(e, 'Non riuscito')); } finally { setAttesa(null); }
  };

  if (dati === false) return <p className="vuoto">Non riesco a leggere quello che abbiamo imparato sulle aste.</p>;
  if (!dati) return <p className="vuoto">Carico le aste…</p>;
  const r = dati.regola || {};
  return (
    <div className="imp-griglia" data-testid="aste">
      <section className="card">
        <div className="ttl"><div className="i r"><Icona nome="avviso" /></div><h2>Aste segnate <small>{dati.segnate}</small></h2>
          <button type="button" className="btn sm" disabled={attesa === 'ripassa'} onClick={ripassa}>{attesa === 'ripassa' ? 'Ripasso…' : 'Ripassa le proposte'}</button></div>
        <p className="imp-nota">
          Un&apos;asta giudiziaria non si propone mai. Quelle che sfuggono al filtro le segna lo staff con «È un&apos;asta»
          negli annunci nuovi, nelle case in archivio e nella scheda della casa: quella casa esce da tutte le proposte,
          l&apos;agenzia viene contata e alla {r.soglia_agenzia === 2 ? 'seconda' : `${r.soglia_agenzia}ª`} asta esce anche lei.
          Da {dati.segnate === 1 ? 'questo annuncio' : `questi ${dati.segnate} annunci`} impariamo le parole nuove, confrontandole
          con {dati.annunci_normali} annunci normali.
        </p>
      </section>

      <section className="card">
        <div className="ttl"><div className="i"><Icona nome="piu" /></div><h2>Parole da aggiungere <small>{dati.proposte.length}</small></h2></div>
        <p className="imp-nota">
          Parole che compaiono in almeno {r.min_aste} annunci segnati (e in almeno il {Math.round((r.quota_aste || 0.2) * 100)}% di quelli
          segnati) e quasi mai negli annunci normali: al massimo una volta ogni {r.ogni_normali} volte che compaiono nelle aste.
          Accettandole valgono {r.punti} punti, come «base d&apos;asta»: da sole bastano a fermare un annuncio.
        </p>
        {!dati.proposte.length && <p className="vuoto">Nessuna parola da aggiungere: per ora quello che segnate lo sapevamo già.</p>}
        <div className="imp-righe">
          {dati.proposte.map((p) => (
            <div key={p.parola} data-testid={`parola-${p.parola.replace(/ /g, '-')}`}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <b>{p.parola}</b>
                <span className="descr" style={{ display: 'block' }}>in {p.aste === 1 ? '1 annuncio segnato' : `${p.aste} annunci segnati`}
                  {p.normali ? ` · in ${p.normali === 1 ? '1 annuncio normale' : `${p.normali} annunci normali`}` : ' · in nessun annuncio normale'}</span>
              </div>
              <div className="azioni">
                <button type="button" className="btn xs p" disabled={attesa === p.parola} onClick={() => parola(p.parola, 'accetta')}>Aggiungi</button>
                <button type="button" className="btn xs" disabled={attesa === p.parola} onClick={() => parola(p.parola, 'scarta')}>No, grazie</button>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="card">
        <div className="ttl"><div className="i g"><Icona nome="fatto" /></div><h2>Parole aggiunte <small>{dati.accettate.length}</small></h2></div>
        {!dati.accettate.length && <p className="vuoto">Nessuna parola aggiunta: valgono solo quelle del filtro.</p>}
        <div className="imp-righe">
          {dati.accettate.map((p) => (
            <div key={p.parola} data-testid={`accettata-${p.parola.replace(/ /g, '-')}`}>
              <div style={{ flex: 1, minWidth: 0 }}><b>{p.parola}</b>
                <span className="descr" style={{ display: 'block' }}>{[p.punti ? `${p.punti} punti` : null, p.chi, quandoBreve(p.quando, true)].filter(Boolean).join(' · ')}</span>
              </div>
              <div className="azioni">
                <button type="button" className="btn xs rosso-vuoto" disabled={attesa === p.parola} onClick={() => parola(p.parola, 'togli')}>Togli</button>
              </div>
            </div>
          ))}
        </div>
        {dati.scartate.length > 0 && (
          <div className="imp-azioni" aria-label="Parole scartate">
            <span className="imp-nota">Scartate:</span>
            {dati.scartate.map((p) => (
              <button key={p} type="button" className="chip" disabled={attesa === p} onClick={() => parola(p, 'togli')} title="Rimettila fra quelle da valutare">{p} ×</button>
            ))}
          </div>
        )}
      </section>

      <section className="card">
        <div className="ttl"><div className="i b"><Icona nome="edificio" /></div><h2>Agenzie contate <small>{dati.agenzie.length}</small></h2></div>
        {!dati.agenzie.length && <p className="vuoto">Nessuna agenzia contata: non è ancora stata segnata nessuna asta.</p>}
        <div className="imp-righe">
          {dati.agenzie.map((a) => (
            <div key={a.id} data-testid={`agenzia-aste-${a.id}`}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <Link className="nome-link" to={`/admin/agenzie/${a.id}`}>{a.etichetta}</Link>
                {a.aste && <span className="tag t-r" style={{ marginLeft: '.4rem' }}>aste</span>}
                <span className="descr" style={{ display: 'block' }}>{a.segnate === 1 ? '1 asta segnata' : `${a.segnate} aste segnate`}
                  {a.aste ? ' · non la proponiamo più' : ` · alla ${r.soglia_agenzia}ª esce da tutte le proposte`}</span>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Aste;
